// Which grid columns the user may hide from the table itself — by dropping a
// column on the remove zone above the table — and bring back from the head's
// columns button. Hiding is the same switch as the table settings checkbox:
// the Head entry's `displayGrid`, kept in `head_${Name}`. Order and widths
// (`gridLayout_${Name}`) are left alone, so a column shown again returns to
// its place at its width.
//
// The head button offers what table settings does: every hidden column but
// those meant for the details dialog (`displayDialogDetails`). react-cabinet
// keeps the columns the user hid apart, because its Heads start technical
// columns hidden among the others; here those are the details columns,
// which table settings already leaves out.

import { isFieldVisibleByRole } from './isFieldVisible';

const HEAD_STORAGE_PREFIX = 'head_';

// The columns of a role's table, as the grid's getVisibleFields picks them.
const visibleToRole = (fields, role) =>
    fields.filter((field) =>
        isFieldVisibleByRole(
            field.cell === 'SelectObject' ? field.object : field.id,
            role
        )
    );

/**
 * Whether the user may hide or show a column at all: the columns table
 * settings lists, less those a Head entry opts out with `disableRemove`.
 * @param {Object} field - Head entry.
 * @returns {boolean} true when the column may be hidden or shown
 */
export const isColumnHideable = (field) =>
    Boolean(field) && !field.displayDialogDetails && !field.disableRemove;

/**
 * Whether column `id` may be dropped on the remove zone now: it is shown,
 * may be hidden, and is not the last column the user sees.
 * @param {Object[]} head - The grid's Head (state).
 * @param {string} id - Column id.
 * @param {string} [role] - The user's system role, for field visibility.
 * @returns {boolean} true when the column may be hidden
 */
export function canHideColumn(head, id, role) {
    const shown = visibleToRole(
        (head || []).filter((field) => field.displayGrid),
        role
    );
    const field = shown.find((f) => f.id === id);
    return isColumnHideable(field) && shown.length > 1;
}

/**
 * Hidden columns the user may show, in Head order; not those the role may
 * not see at all.
 * @param {Object[]} head - The grid's Head (state).
 * @param {string} [role] - The user's system role.
 * @returns {Object[]} Head entries
 */
export const getHiddenColumns = (head, role) =>
    visibleToRole(
        (head || []).filter(
            (field) => !field.displayGrid && isColumnHideable(field)
        ),
        role
    );

/**
 * Ids of the columns a Head shows, for taking a change of visibility back.
 * @param {Object[]} head - The grid's Head (state).
 * @returns {string[]} ids of columns with `displayGrid`
 */
export const getShownColumnIds = (head) =>
    head.filter((field) => field.displayGrid).map((field) => field.id);

/**
 * Shows or hides columns.
 * @param {Object[]} head - The grid's Head (state).
 * @param {string[]} ids - Columns to change; ones that may not be hidden or
 *   shown are skipped.
 * @param {boolean} visible - Show (true) or hide (false).
 * @returns {Object[]} a new Head; the same array when nothing changed
 */
export function setColumnsVisible(head, ids, visible) {
    const wanted = new Set(ids);
    let changed = false;
    const next = head.map((field) => {
        if (
            !wanted.has(field.id) ||
            !isColumnHideable(field) ||
            Boolean(field.displayGrid) === visible
        ) {
            return field;
        }
        changed = true;
        return { ...field, displayGrid: visible };
    });
    return changed ? next : head;
}

/**
 * A Head showing exactly the given hideable columns; columns the user may not
 * hide or show keep their visibility.
 * @param {Object[]} head - The grid's Head (state).
 * @param {string[]} shownIds - Ids from `getShownColumnIds`.
 * @returns {Object[]} a new Head; the same array when nothing changed
 */
export function restoreShownColumns(head, shownIds) {
    const shown = new Set(shownIds);
    const ids = head.map((field) => field.id);
    return setColumnsVisible(
        setColumnsVisible(head, ids.filter((id) => !shown.has(id)), false),
        ids.filter((id) => shown.has(id)),
        true
    );
}

/**
 * Keeps the Head's visibility where table settings and the pages reading
 * `head_${Name}` (mergeHeadWithStorage) find it. Storage errors (quota,
 * private mode) are swallowed — the change then lasts until the page is
 * left.
 * @param {string} name - Grid name (`this.Name`).
 * @param {Object[]} head - The grid's Head (state).
 * @returns {void}
 */
export function saveHeadVisibility(name, head) {
    try {
        localStorage.setItem(HEAD_STORAGE_PREFIX + name, JSON.stringify(head));
    } catch (e) {
        // ignore
    }
}
